// Removed axios import - using native fetch instead
import * as xml2js from 'xml2js';
import { RSSFeed, RSSItem } from '../types/rss';
import indexedDBService from './indexedDBService';

// URL do feed RSS da Folha
const RSS_URL = 'https://feeds.folha.uol.com.br/emcimadahora/rss091.xml';

// Proxy CORS para permitir requisições cross-origin
const CORS_PROXY = 'https://corsproxy.io/?';

/**
 * Tenta decodificar o buffer com diferentes encodings para garantir UTF-8 correto
 */
const decodeWithMultipleEncodings = async (buffer: ArrayBuffer): Promise<string> => {
  // Lista de encodings para tentar
  const encodings = ['utf-8', 'iso-8859-1', 'windows-1252', 'latin1'];
  
  for (const encoding of encodings) {
    try {
      const decoder = new TextDecoder(encoding, { fatal: false });
      const text = decoder.decode(buffer);
      
      // Verifica se o texto contém caracteres de substituição (indicando encoding incorreto)
      if (!text.includes('�') && !text.includes('\uFFFD')) {
        // Se o encoding não é UTF-8, converte para UTF-8
        if (encoding !== 'utf-8') {
          // Re-codifica para UTF-8
          const utf8Encoder = new TextEncoder();
          const utf8Buffer = utf8Encoder.encode(text);
          const utf8Decoder = new TextDecoder('utf-8');
          return utf8Decoder.decode(utf8Buffer);
        }
        return text;
      }
    } catch (error) {
      // Continua para o próximo encoding
      continue;
    }
  }
  
  // Se todos falharam, usa UTF-8 como fallback
  const fallbackDecoder = new TextDecoder('utf-8', { fatal: false });
  return fallbackDecoder.decode(buffer);
};

/**
 * Busca e processa o feed RSS com cache IndexedDB
 */
export const fetchRSSFeed = async (url: string = RSS_URL): Promise<RSSFeed> => {
  try {
    // Tenta buscar do cache primeiro (cache válido por 3 minutos)
    const cachedFeed = await indexedDBService.getFeed(url, 3 * 60 * 1000);
    if (cachedFeed) {
      return cachedFeed;
    }

    console.log('Buscando feed RSS da fonte:', url);
    
    // Faz a requisição para o feed RSS através do proxy CORS
    const response = await fetch(`${CORS_PROXY}url=${encodeURIComponent(url)}`, {
      headers: {
        'Accept': 'application/rss+xml, application/xml, text/xml',
        'Accept-Charset': 'utf-8',
        'Content-Type': 'text/xml; charset=utf-8'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // Tenta diferentes métodos de decodificação para garantir UTF-8 correto
    const buffer = await response.arrayBuffer();
    const xmlData = await decodeWithMultipleEncodings(buffer);
    
    // Converte o XML para JSON com configurações otimizadas para UTF-8
    const parser = new xml2js.Parser({ 
      explicitArray: false,
      trim: true,
      normalize: true,
      normalizeTags: false,
      attrkey: 'ATTR',
      charkey: 'value',
      explicitCharkey: false,
      mergeAttrs: false
    });
    const result = await parser.parseStringPromise(xmlData);
    
    // Extrai os dados do feed
    const channel = result.rss.channel;
    
    // Processa os itens do feed
    const items: RSSItem[] = Array.isArray(channel.item) 
      ? channel.item.map(processRSSItem)
      : [processRSSItem(channel.item)];
    
    console.log(`Feed RSS processado: ${items.length} itens encontrados`);
    console.log('Primeiros 3 títulos:', items.slice(0, 3).map(item => item.title));
    
    const feedData: RSSFeed = {
      title: channel.title,
      description: channel.description,
      link: channel.link,
      lastBuildDate: channel.lastBuildDate,
      items
    };

    // Salva no cache IndexedDB
    try {
      await indexedDBService.saveFeed(url, feedData);
    } catch (cacheError) {
      console.warn('Erro ao salvar no cache, continuando sem cache:', cacheError);
    }

    return feedData;
  } catch (error) {
    console.error('Erro ao buscar feed RSS:', error);
    throw new Error('Não foi possível carregar o feed RSS');
  }
};

/**
 * Processa um item do feed RSS
 */
const processRSSItem = (item: any): RSSItem => {
  // Extrai a URL da imagem da descrição, se existir
  let imageUrl: string | undefined;
  let content = item.description;
  
  // Tenta extrair a URL da imagem da descrição usando regex
  if (content) {
    const imgMatch = content.match(/<img[^>]+src="([^"]+)"[^>]*>/i);
    if (imgMatch && imgMatch[1]) {
      imageUrl = imgMatch[1];
    }
  }
  
  return {
    title: fixEncoding(item.title || 'Sem título'),
    link: item.link || '',
    description: cleanHtml(fixEncoding(item.description)) || '',
    pubDate: item.pubDate || '',
    content: fixEncoding(content) || '',
    imageUrl
  };
};

/**
 * Corrige problemas de encoding em strings
 */
const fixEncoding = (text: string): string => {
  if (!text) return '';
  
  // Corrige double-encoding comum (UTF-8 interpretado como ISO-8859-1)
  try {
    // Se o texto contém sequências que parecem double-encoded
    if (text.includes('Ã') || text.includes('â') || text.includes('Â')) {
      // Tenta decodificar como se fosse ISO-8859-1 e depois re-codificar como UTF-8
      const bytes = new Uint8Array(text.length);
      for (let i = 0; i < text.length; i++) {
        bytes[i] = text.charCodeAt(i) & 0xFF;
      }
      const decoder = new TextDecoder('utf-8', { fatal: false });
      const corrected = decoder.decode(bytes);
      
      // Se a correção resultou em texto válido, usa ela
      if (!corrected.includes('�')) {
        return corrected;
      }
    }
  } catch (error) {
    // Se falhar, retorna o texto original
  }
  
  return text;
};

/**
 * Remove tags HTML do texto e decodifica entidades HTML
 */
const cleanHtml = (html: string): string => {
  if (!html) return '';
  
  // Primeiro, normaliza caracteres problemáticos comuns
  let text = html
    // Corrige caracteres mal codificados comuns
    .replace(/Ã¡/g, 'á')
    .replace(/Ã /g, 'à')
    .replace(/Ã£/g, 'ã')
    .replace(/Ã¢/g, 'â')
    .replace(/Ã©/g, 'é')
    .replace(/Ãª/g, 'ê')
    .replace(/Ã­/g, 'í')
    .replace(/Ã³/g, 'ó')
    .replace(/Ãµ/g, 'õ')
    .replace(/Ã´/g, 'ô')
    .replace(/Ãº/g, 'ú')
    .replace(/Ã§/g, 'ç')
    .replace(/Ã\u0081/g, 'Á')
    .replace(/Ã\u0080/g, 'À')
    .replace(/Ã\u0083/g, 'Ã')
    .replace(/Ã\u0082/g, 'Â')
    .replace(/Ã\u0089/g, 'É')
    .replace(/Ã\u008a/g, 'Ê')
    .replace(/Ã\u008d/g, 'Í')
    .replace(/Ã\u0093/g, 'Ó')
    .replace(/Ã\u0095/g, 'Õ')
    .replace(/Ã\u0094/g, 'Ô')
    .replace(/Ã\u009a/g, 'Ú')
    .replace(/Ã\u0087/g, 'Ç');
  
  // Decodifica entidades HTML numéricas
  text = text
    // Decodifica entidades numéricas decimais
    .replace(/&#(\d+);/g, (match, dec) => {
      try {
        return String.fromCharCode(parseInt(dec, 10));
      } catch {
        return match;
      }
    })
    // Decodifica entidades numéricas hexadecimais
    .replace(/&#x([0-9a-fA-F]+);/g, (match, hex) => {
      try {
        return String.fromCharCode(parseInt(hex, 16));
      } catch {
        return match;
      }
    });
  
  // Cria um elemento temporário para decodificar entidades HTML nomeadas
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = text;
  
  // Extrai o texto sem tags HTML
  text = tempDiv.textContent || tempDiv.innerText || '';
  
  // Substitui entidades HTML comuns que podem não ter sido decodificadas
  text = text
    .replace(/&nbsp;/gi, ' ') // Substitui &nbsp; por espaço
    .replace(/&amp;/gi, '&') // Substitui &amp; por &
    .replace(/&lt;/gi, '<') // Substitui &lt; por <
    .replace(/&gt;/gi, '>') // Substitui &gt; por >
    .replace(/&quot;/gi, '"') // Substitui &quot; por "
    .replace(/&#39;/gi, "'") // Substitui &#39; por '
    .replace(/&apos;/gi, "'") // Substitui &apos; por '
    // Caracteres acentuados minúsculos
    .replace(/&aacute;/gi, 'á')
    .replace(/&agrave;/gi, 'à')
    .replace(/&atilde;/gi, 'ã')
    .replace(/&acirc;/gi, 'â')
    .replace(/&eacute;/gi, 'é')
    .replace(/&egrave;/gi, 'è')
    .replace(/&ecirc;/gi, 'ê')
    .replace(/&iacute;/gi, 'í')
    .replace(/&igrave;/gi, 'ì')
    .replace(/&icirc;/gi, 'î')
    .replace(/&oacute;/gi, 'ó')
    .replace(/&ograve;/gi, 'ò')
    .replace(/&otilde;/gi, 'õ')
    .replace(/&ocirc;/gi, 'ô')
    .replace(/&uacute;/gi, 'ú')
    .replace(/&ugrave;/gi, 'ù')
    .replace(/&ucirc;/gi, 'û')
    .replace(/&ccedil;/gi, 'ç')
    // Caracteres acentuados maiúsculos
    .replace(/&Aacute;/gi, 'Á')
    .replace(/&Agrave;/gi, 'À')
    .replace(/&Atilde;/gi, 'Ã')
    .replace(/&Acirc;/gi, 'Â')
    .replace(/&Eacute;/gi, 'É')
    .replace(/&Egrave;/gi, 'È')
    .replace(/&Ecirc;/gi, 'Ê')
    .replace(/&Iacute;/gi, 'Í')
    .replace(/&Igrave;/gi, 'Ì')
    .replace(/&Icirc;/gi, 'Î')
    .replace(/&Oacute;/gi, 'Ó')
    .replace(/&Ograve;/gi, 'Ò')
    .replace(/&Otilde;/gi, 'Õ')
    .replace(/&Ocirc;/gi, 'Ô')
    .replace(/&Uacute;/gi, 'Ú')
    .replace(/&Ugrave;/gi, 'Ù')
    .replace(/&Ucirc;/gi, 'Û')
    .replace(/&Ccedil;/gi, 'Ç')
    // Remove múltiplos espaços e normaliza
    .replace(/\s+/g, ' ')
    .trim();
  
  // Remove "Leia mais" e padrões de data/hora do final do texto
  text = text
    .replace(/\s*Leia mais.*$/gi, '') // Remove "Leia mais" e tudo após
    .replace(/\s*\(\d{2}\/\d{2}\/\d{4}\s*-\s*\d{1,2}h\d{2}.*$/gi, '') // Remove padrões de data/hora
    .replace(/\s*\d{2}\/\d{2}\/\d{4}\s*-\s*\d{1,2}h\d{2}.*$/gi, '') // Remove padrões de data/hora sem parênteses
    .trim();
    
  return text;
};